'use client';

import { useEffect, useRef, useState } from 'react';

/** Hook: returns true once the referenced element has entered the viewport
 * (or is near it via rootMargin). For lazy-mounting heavy third-party embeds
 * — e.g. Wistia, Vimeo, Loom — so their runtime scripts don't execute on
 * initial page load for blocks the visitor never scrolls to. Disconnects on
 * first intersection so we don't keep observing once mounted. Above-the-fold
 * elements fire on the first observer callback (~1 microtask after mount),
 * so this adds no perceptible delay for visible content.
 *
 * The default `rootMargin` is intentionally generous (1000px ahead) because
 * the heaviest embed we lazy-load — Wistia — takes 1-2s on Slow 4G to load
 * its runtime and render a thumbnail. Triggering mount only when the element
 * scrolls into view means the visitor stares at an empty container for the
 * loading window. With 1000px (~1.2 mobile viewport heights), the embed
 * starts loading well before the visitor scrolls close enough to see the
 * placeholder, so by the time the container is on screen the content is
 * already there. */
function useNearViewport(rootMargin = '1000px 0px') {
  const ref = useRef<HTMLDivElement>(null);
  const [intersected, setIntersected] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === 'undefined') {
      // Older browsers without IO: load immediately rather than not at all.
      setIntersected(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setIntersected(true);
            io.disconnect();
            break;
          }
        }
      },
      { rootMargin },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [rootMargin]);

  return { ref, intersected };
}

/**
 * Lightweight YouTube facade — shows a thumbnail + play button.
 * Only loads the heavy iframe when the user clicks play.
 * Saves ~800KB of JS/CSS per YouTube embed on initial page load.
 *
 * `className` and `style` are applied to the root element so the parent can
 * control responsive aspect-ratio and border-radius via CSS custom properties.
 */
export function YouTubeFacade({
  videoId,
  embedUrl,
  className,
  style,
}: {
  videoId: string;
  embedUrl: string;
  className?: string;
  style?: React.CSSProperties;
}) {
  const [activated, setActivated] = useState(false);
  const thumbRef = useRef<HTMLImageElement>(null);
  const [thumbLoaded, setThumbLoaded] = useState(false);

  useEffect(() => {
    if (thumbRef.current?.complete) setThumbLoaded(true);
  }, []);

  if (activated) {
    return (
      <div className={className} style={style}>
        <iframe
          src={`${embedUrl}${embedUrl.includes('?') ? '&' : '?'}autoplay=1`}
          title="Video"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }}
        />
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setActivated(true)}
      aria-label="Play video"
      className={className}
      style={{
        ...style,
        border: 'none',
        padding: 0,
        cursor: 'pointer',
        background: '#000',
        display: 'block',
      }}
    >
      {/* YouTube thumbnail */}
      <img
        ref={thumbRef}
        src={`https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`}
        alt="Video thumbnail"
        loading="lazy"
        decoding="async"
        onLoad={() => setThumbLoaded(true)}
        className={`sp-thumb-fade${thumbLoaded ? ' sp-thumb-fade-in' : ''}`}
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '100%',
          height: '100%',
          objectFit: 'cover',
        }}
      />
      {/* Play button overlay */}
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '68px',
          height: '48px',
          borderRadius: '14px',
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'background-color 200ms',
        }}
      >
        <svg viewBox="0 0 24 24" width="28" height="28" fill="#fff" stroke="none">
          <polygon points="6 3 20 12 6 21 6 3" />
        </svg>
      </div>
    </button>
  );
}

/** Renders custom HTML and executes embedded scripts.
 *
 * Mount is gated by IntersectionObserver: the customHtml + its scripts are
 * only injected once the container enters (or comes within 1000px of) the
 * viewport. For pages with many heavy embeds (a 20-Wistia testimonial
 * gallery, say) this means the player runtime only loads for the videos a
 * visitor actually scrolls to.
 *
 * Fade-in pairs with content arrival, not with the IO trigger:
 *  - On intersect, the container gets `.sp-lazy-fade-in` → opacity 0 +
 *    slight translateY, with a 250ms transition prepared.
 *  - A MutationObserver watches the container for the moment Wistia (or
 *    any other mounted script) inserts its real visible content — img,
 *    iframe, video, or a defined `<wistia-player>` shadow root. At that
 *    moment we add `.sp-lazy-fade-in-ready` → opacity 1, transition fires.
 *  - If the embed's customHtml already includes a wistia-player tag at
 *    inject time, contentReady is set immediately so the hero doesn't
 *    wait on the fade.
 *
 * `className` and `style` go on the container so the parent can apply
 * responsive aspect-ratio and border-radius via CSS variables. */
export function CustomHtmlEmbed({
  html,
  className,
  style,
}: {
  html: string;
  className?: string;
  style?: React.CSSProperties;
}) {
  const { ref: containerRef, intersected } = useNearViewport();
  const [contentReady, setContentReady] = useState(false);

  useEffect(() => {
    if (!intersected || !containerRef.current || !html) return;

    containerRef.current.innerHTML = '';

    // Parse HTML and extract scripts
    const temp = document.createElement('div');
    temp.innerHTML = html;

    const scripts = temp.querySelectorAll('script');
    const scriptContents: { src?: string; content?: string }[] = [];

    scripts.forEach((script) => {
      scriptContents.push({
        src: script.src || undefined,
        content: script.textContent || undefined,
      });
      script.remove();
    });

    // Insert non-script HTML
    containerRef.current.innerHTML = temp.innerHTML;

    // Execute scripts in order
    scriptContents.forEach(({ src, content }) => {
      const newScript = document.createElement('script');
      newScript.type = 'text/javascript';

      if (src) {
        newScript.src = src;
        newScript.async = true;
      } else if (content) {
        newScript.textContent = content;
      }

      containerRef.current?.appendChild(newScript);
    });

    const container = containerRef.current;
    return () => {
      if (container) {
        container.innerHTML = '';
      }
    };
  }, [html, intersected, containerRef]);

  // Watch for visible content arrival so we can flip on the fade-to-1 class
  // at the right moment. We only care about elements that actually paint
  // something (img / iframe / video) or a wistia-player web component that
  // has had its custom element definition register.
  useEffect(() => {
    if (!intersected || !containerRef.current) return;
    const container = containerRef.current;

    const hasVisible = () =>
      !!container.querySelector('img, iframe, video, wistia-player');

    if (hasVisible()) {
      // Embed already includes a renderable element (e.g. <wistia-player>
      // in the customHtml itself). No need to wait for an injection.
      setContentReady(true);
      return;
    }

    const mo = new MutationObserver(() => {
      if (hasVisible()) {
        setContentReady(true);
        mo.disconnect();
      }
    });
    mo.observe(container, { childList: true, subtree: true });
    // Safety net: even if no element ever appears (e.g. the embed's a
    // pure-script analytics snippet with no UI), flip to ready after 3s so
    // the container doesn't stay invisible forever.
    const safetyTimer = window.setTimeout(() => setContentReady(true), 3000);
    return () => {
      mo.disconnect();
      window.clearTimeout(safetyTimer);
    };
  }, [intersected, containerRef]);

  const stateClass = intersected
    ? contentReady
      ? ' sp-lazy-fade-in sp-lazy-fade-in-ready'
      : ' sp-lazy-fade-in'
    : '';

  return (
    <div
      ref={containerRef}
      className={`${className ?? ''} [&_iframe]:w-full [&_iframe]:h-full [&_iframe]:border-none${stateClass}`}
      style={style}
    />
  );
}
